import React, { useState } from 'react';
import {
    Box,
    Button,
    HStack,
    Icon,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    PopoverHeader,
    Menu,
    MenuButton,
    MenuList,
    MenuItem as ChakraMenuItem,
    Radio,
    Switch,
    Text,
    VStack,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { HiOutlineClipboardCopy, HiOutlineCheck } from 'react-icons/hi';
import { MenuViewProps } from './commonMenuProps';
import {
    MenuItemActionButtonProps,
    MenuItemProps,
    MenuItemFolderProps,
    MenuItemLinkProps,
} from './item';
import { assertNever } from '@varos/util-typescript';
import { MenuActionButtonProps, MenuActionProps } from './action';
import { CommonMenuConfig } from './commonMenuConfig';

export function createMenuView(config: CommonMenuConfig = {}): React.FC<MenuViewProps> {
    const LinkItem: React.FC<MenuItemLinkProps> = (props) => {
        const [copied, setCopied] = useState(false);

        const handleCopy = () => {
            navigator.clipboard.writeText(props.link.value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };

        return (
            <Box
                as="button"
                w="full"
                onClick={handleCopy}
                cursor="pointer"
                borderRadius="md"
                _hover={{
                    bg: 'whiteAlpha.50',
                }}
                _active={{
                    bg: 'whiteAlpha.100',
                }}
                transition="background-color 0.2s"
            >
                <InputGroup size="sm" w="full" pointerEvents="none">
                    <Input
                        value={props.link.value}
                        isReadOnly
                        bg="whiteAlpha.50"
                        borderColor="whiteAlpha.200"
                        color="whiteAlpha.900"
                        fontSize="xs"
                        cursor="pointer"
                        fontFamily="mono"
                        _placeholder={{
                            color: 'whiteAlpha.500',
                        }}
                        _hover={{
                            borderColor: 'whiteAlpha.300',
                            bg: 'whiteAlpha.100',
                        }}
                        _focus={{
                            borderColor: 'whiteAlpha.200',
                            boxShadow: 'none',
                            bg: 'whiteAlpha.50',
                        }}
                        _readOnly={{
                            cursor: 'pointer',
                            _hover: {
                                borderColor: 'whiteAlpha.300',
                                bg: 'whiteAlpha.100',
                            },
                            _focus: {
                                borderColor: 'whiteAlpha.200',
                                boxShadow: 'none',
                                bg: 'whiteAlpha.50',
                            },
                        }}
                    />
                    <InputRightElement>
                        <Icon
                            as={copied ? HiOutlineCheck : HiOutlineClipboardCopy}
                            fontSize="sm"
                            color={copied ? 'blue.300' : 'whiteAlpha.500'}
                            transition="color 0.2s"
                        />
                    </InputRightElement>
                </InputGroup>
            </Box>
        );
    };

    const MenuAction: React.FC<Exclude<MenuActionProps, MenuActionButtonProps>> = (
        props
    ) => {
        if (props.kind === 'switch') {
            return <Switch {...props.switch} colorScheme="blue" size="sm" />;
        }
        if (props.kind === 'select') {
            const currentOption = props.options.find(
                (option) => option.value === props.select.value
            );

            return (
                <Menu placement="bottom-end">
                    <MenuButton
                        as={Button}
                        variant="ghost"
                        size="sm"
                        rightIcon={<ChevronRightIcon fontSize="md" />}
                        bg="none"
                        border="none"
                        color="whiteAlpha.500"
                        fontWeight="medium"
                        px={0}
                        minW="auto"
                        h="auto"
                        iconSpacing={1}
                        _hover={{
                            bg: 'none',
                            color: 'whiteAlpha.600',
                        }}
                        _expanded={{
                            bg: 'none',
                            color: 'whiteAlpha.600',
                            '& .chakra-button__icon': {
                                transform: 'rotate(90deg)',
                            },
                        }}
                        _focus={{
                            boxShadow: 'none',
                        }}
                        sx={{
                            '& .chakra-button__icon': {
                                transition: 'transform 0.2s ease',
                            },
                        }}
                    >
                        {currentOption?.label || 'Select...'}
                    </MenuButton>
                    <MenuList
                        bg="black"
                        w="auto"
                        minW="12rem"
                        fontSize="sm"
                        p={0}
                        overflow="hidden"
                        _focus={{
                            outline: 'none',
                            boxShadow: 'none',
                        }}
                    >
                        <VStack
                            align="start"
                            w="full"
                            bg="whiteAlpha.200"
                            p={1}
                            spacing={0}
                        >
                            {props.options.map((option) => {
                                const isSelected = option.value === props.select.value;
                                return (
                                    <ChakraMenuItem
                                        key={option.value}
                                        as={HStack}
                                        onClick={() => {
                                            const event = {
                                                target: { value: option.value },
                                            } as React.ChangeEvent<HTMLSelectElement>;
                                            props.select.onChange?.(event);
                                        }}
                                        borderRadius="md"
                                        py={1}
                                        px={2}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color="whiteAlpha.800"
                                        bg="none"
                                        spacing={6}
                                        justify="space-between"
                                        _hover={{
                                            bg: 'whiteAlpha.50',
                                        }}
                                    >
                                        <Text>{option.label}</Text>
                                        {isSelected && (
                                            <Radio
                                                isChecked={true}
                                                isReadOnly={true}
                                                colorScheme="blue"
                                                size="sm"
                                            />
                                        )}
                                    </ChakraMenuItem>
                                );
                            })}
                        </VStack>
                    </MenuList>
                </Menu>
            );
        }
        assertNever(props);
    };

    const MenuItem: React.FC<MenuItemProps & { variant?: 'default' | 'compact' }> = (
        props
    ) => {
        const isCompact = props.variant === 'compact';
        const itemPadding = isCompact ? { py: 1, px: 3 } : { py: 2, px: 3 };
        const itemSpacing = isCompact ? 0 : 2;

        if (props.kind === 'divider') {
            return <Box w="full" h="1px" bg="whiteAlpha.100" my={1} />;
        }

        if (props.kind === 'link') {
            return (
                <VStack
                    w="full"
                    align="start"
                    borderRadius="md"
                    {...itemPadding}
                    fontSize="sm"
                    fontWeight="medium"
                    color="whiteAlpha.800"
                    spacing={itemSpacing}
                >
                    {(props.label || props.Icon) && (
                        <HStack spacing={isCompact ? 2 : 3} userSelect="none" w="full">
                            {props.label && (
                                <VStack align="start" spacing={0} flex={1}>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="medium"
                                        color="whiteAlpha.600"
                                    >
                                        {props.label}
                                    </Text>
                                </VStack>
                            )}
                        </HStack>
                    )}
                    <Box w="full">
                        <LinkItem {...props} />
                    </Box>
                </VStack>
            );
        }

        if (props.kind === 'folder') {
            return (
                <Menu placement="right-start" offset={[8, 0]}>
                    <MenuButton
                        as={Button}
                        variant="unstyled"
                        size="sm"
                        w="full"
                        borderRadius="md"
                        {...itemPadding}
                        fontSize="sm"
                        fontWeight="medium"
                        color="whiteAlpha.800"
                        _hover={{
                            bg: 'whiteAlpha.50',
                        }}
                        _expanded={{
                            bg: 'whiteAlpha.100',
                            '& .chakra-button__icon': {
                                transform: 'rotate(90deg)',
                            },
                        }}
                        sx={{
                            '& .chakra-button__icon': {
                                transition: 'transform 0.2s ease',
                            },
                        }}
                    >
                        <HStack w="full" justify="space-between" spacing={3}>
                            <HStack spacing={isCompact ? 2 : 3}>
                                <Icon fontSize="lg" as={props.Icon} />
                                <VStack align="start" spacing={0}>
                                    <Text
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color="whiteAlpha.800"
                                    >
                                        {props.label}
                                    </Text>
                                </VStack>
                            </HStack>
                            <Icon
                                className="chakra-button__icon"
                                as={ChevronRightIcon}
                                color="whiteAlpha.500"
                                fontSize="md"
                            />
                        </HStack>
                    </MenuButton>
                    <MenuList minW="12rem" bg="black" p={0} overflow="hidden">
                        <VStack
                            align="start"
                            w="full"
                            bg="whiteAlpha.200"
                            p={1}
                            spacing={0}
                        >
                            {props.items.map((subItem, subIndex) => {
                                if (subItem.kind === 'divider') {
                                    return (
                                        <Box
                                            key={subIndex}
                                            w="full"
                                            h="1px"
                                            bg="whiteAlpha.100"
                                            my={1}
                                        />
                                    );
                                }

                                if (subItem.action.kind === 'button') {
                                    return (
                                        <ChakraMenuItem
                                            key={subIndex}
                                            borderRadius="md"
                                            py={1}
                                            px={2}
                                            userSelect="none"
                                            onClick={(event) => {
                                                if (subItem.action.kind === 'button') {
                                                    subItem.action.onClick(event as any);
                                                }
                                            }}
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={
                                                subItem.colorScheme === 'red'
                                                    ? 'red.400'
                                                    : 'whiteAlpha.800'
                                            }
                                            _hover={{
                                                bg: 'whiteAlpha.50',
                                            }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon fontSize="lg" as={subItem.Icon} />
                                                <Text>{subItem.label}</Text>
                                            </HStack>
                                        </ChakraMenuItem>
                                    );
                                }

                                // Handle other action types for submenu items if needed
                                return null;
                            })}
                        </VStack>
                    </MenuList>
                </Menu>
            );
        }

        const action = props.action;
        if (action.kind === 'button') {
            return (
                <Box
                    as="button"
                    onClick={action.onClick}
                    w="full"
                    borderRadius="md"
                    {...itemPadding}
                    fontSize="sm"
                    fontWeight="medium"
                    color={props.colorScheme === 'red' ? 'red.400' : 'whiteAlpha.800'}
                    bg="none"
                    border="none"
                    cursor="pointer"
                    _hover={{
                        bg: 'whiteAlpha.50',
                    }}
                >
                    <VStack w="full" align="start" spacing={itemSpacing}>
                        <HStack spacing={isCompact ? 1 : 2} userSelect="none">
                            <Icon fontSize="lg" as={props.Icon} />
                            <VStack align="start" spacing={0}>
                                <Text
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color={
                                        props.colorScheme === 'red'
                                            ? 'red.400'
                                            : 'whiteAlpha.800'
                                    }
                                >
                                    {props.label}
                                </Text>
                                {props.secondary && (
                                    <Text fontSize="xs" color="whiteAlpha.500">
                                        {props.secondary}
                                    </Text>
                                )}
                            </VStack>
                        </HStack>
                    </VStack>
                </Box>
            );
        }
        const handleClick =
            props.kind === 'action' && props.action.kind === 'button'
                ? (e: React.MouseEvent<HTMLDivElement>) => {
                      if (props.action.kind === 'button') {
                          props.action.onClick(e as any);
                      }
                  }
                : undefined;

        return (
            <HStack
                onClick={handleClick}
                w="full"
                justify="space-between"
                align="center"
                borderRadius="md"
                {...itemPadding}
                fontSize="sm"
                fontWeight="medium"
                color="whiteAlpha.800"
                spacing={12}
                _hover={{
                    bg: 'whiteAlpha.50',
                }}
            >
                <HStack spacing={isCompact ? 2 : 3} flex={1} userSelect="none">
                    <Icon fontSize="lg" as={props.Icon} />
                    <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium" color="whiteAlpha.800">
                            {props.label}
                        </Text>
                        {props.secondary && (
                            <Text fontSize="xs" color="whiteAlpha.500">
                                {props.secondary}
                            </Text>
                        )}
                    </VStack>
                </HStack>
                <MenuAction {...action} />
            </HStack>
        );
    };

    return (props) => {
        const variant = props.variant || 'default';

        return (
            <Popover
                {...props.popover}
                size="2xl"
                closeOnBlur={true}
                closeDelay={0}
                placement={props.placement}
            >
                <PopoverTrigger>{props.children}</PopoverTrigger>
                <PopoverContent
                    w="auto"
                    minW={props.minW}
                    fontSize="sm"
                    p={0}
                    bg="black"
                    _focus={{
                        outline: 'none',
                        boxShadow: 'none',
                    }}
                >
                    {props.header && (
                        <PopoverHeader
                            as={HStack}
                            border="none"
                            bg="whiteAlpha.200"
                            px={3}
                            py={2}
                            color="whiteAlpha.600"
                            fontWeight="semibold"
                            justify="space-between"
                            borderTopLeftRadius="md"
                            borderTopRightRadius="md"
                        >
                            <Text>{props.header.label}</Text>
                        </PopoverHeader>
                    )}
                    <PopoverBody
                        as={VStack}
                        border="none"
                        p={1}
                        bg="whiteAlpha.200"
                        align="start"
                        spacing={0}
                        borderBottomLeftRadius="md"
                        borderBottomRightRadius="md"
                        borderTopLeftRadius={props.header ? '0' : 'md'}
                        borderTopRightRadius={props.header ? '0' : 'md'}
                    >
                        {props.items.map((item, index) => (
                            <MenuItem key={index} {...item} variant={variant} />
                        ))}
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        );
    };
}
